/**
 * Issuer flow — turn a tender pack into a defensible evaluation model.
 *
 * Intake → agent extraction → weighting model → publish. The weighting step
 * validates against the Procurement Act 2023 regime the tender declares:
 * criteria must total 100%, and social value must carry at least the PPN 002
 * minimum of 10% for in-scope contracting authorities.
 */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../styles/theme';
import { Button } from '../styles/GlobalStyles';
import {
  FileText, Sparkles, Scale, Send, CheckCircle2, AlertTriangle, ArrowRight,
  ArrowLeft, RotateCcw, Info, ShieldCheck, Layers, Search, Check, Database,
  UploadCloud, X,
} from 'lucide-react';
import FinalReportViewer from '../components/FinalReportViewer';
import AgentRunConsole from '../components/evaluation/AgentRunConsole';
import {
  Shell, Eyebrow, PageTitle, PageSub, HeaderRow, ContextBar, ContextGrid, ContextItem,
  Steps, Panel, PanelHead, PanelTitle, PanelNote, PanelBody, Split, MicroLabel, Mono,
  StatTile, TileRow, Tag, Confidence, Cite, Meter, Legend, LegendItem, SEG_COLORS,
  Table, TableScroll, Actions, Callout, Chevron, Num, Processing,
} from '../components/evaluation/EvalKit';
import { Input } from '../styles/GlobalStyles';
import {
  tender, criteria, scoringScale, issuerAgents, issuerLog, ittEvidence,
  tenderBank, requiredDocIds,
} from '../data/evaluationMocks';

const STEPS = ['Tender package', 'Agent extraction', 'Weighting model', 'Publish model'];

const DocRow = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  &:last-child { border-bottom: 0; }
`;

const DocIcon = styled.div`
  width: 34px; height: 34px; border-radius: 9px;
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[500]};
  display: grid; place-items: center; flex: 0 0 auto;
`;

const DocName = styled.div`
  font-size: 13px; font-weight: 600; color: ${theme.colors.textPrimary};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const DocMeta = styled.div`font-size: 11.5px; color: ${theme.colors.textMuted}; font-variant-numeric: tabular-nums;`;

const SourceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[5]};
  margin-bottom: ${theme.spacing[5]};
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const SourceCard = styled.button`
  text-align: left;
  padding: ${theme.spacing[6]};
  border: 1px solid ${theme.colors.border};
  border-radius: 16px;
  background: ${theme.colors.surface};
  cursor: pointer;
  transition: border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    border-color: ${theme.colors.primary[300]};
    transform: translateY(-2px);
    box-shadow: 0 14px 34px -24px rgba(0,0,82,.45);
  }
  &:focus-visible { outline: 2px solid ${theme.colors.primary[400]}; outline-offset: 2px; }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover { transform: none; }
  }
`;

const SourceIcon = styled.span`
  width: 40px; height: 40px; border-radius: 11px;
  display: grid; place-items: center;
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const SourceTitle = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 16px; font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin-bottom: 5px;
  display: flex; align-items: center; gap: 8px;
`;

const SourceBody = styled.p`
  font-size: 13px; line-height: 1.6;
  color: ${theme.colors.textMuted};
  margin: 0 0 ${theme.spacing[3]};
`;

const SourceMeta = styled.div`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 10.5px;
  color: ${theme.colors.textDisabled};
`;

const ChangeSource = styled.button`
  background: none; border: 0; cursor: pointer;
  font-size: 12px; font-weight: 600;
  color: ${theme.colors.primary[500]};
  display: inline-flex; align-items: center; gap: 5px;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const Drop = styled.label<{ $over: boolean }>`
  display: block;
  margin: ${theme.spacing[5]};
  padding: ${theme.spacing[10]} ${theme.spacing[6]};
  border: 1.5px dashed ${p => (p.$over ? theme.colors.primary[400] : theme.colors.borderHover)};
  background: ${p => (p.$over ? theme.colors.primary[50] : theme.colors.backgroundSecondary)};
  border-radius: 14px;
  text-align: center;
  cursor: pointer;
  transition: border-color 140ms ease, background 140ms ease;
  &:hover { border-color: ${theme.colors.primary[300]}; }
`;

const DropTitle = styled.div`
  font-size: 14px; font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin: ${theme.spacing[3]} 0 4px;
`;

const DropHint = styled.div`
  font-size: 12px; color: ${theme.colors.textMuted};
`;

const UpRow = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  &:last-child { border-bottom: 0; }
`;

const PickerBar = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  position: relative; flex: 1; min-width: 200px;
  svg { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: ${theme.colors.textMuted}; }
`;

const SearchBox = styled(Input)`
  padding-left: 34px;
  height: 34px;
  font-size: 13px;
`;

const FilterSel = styled.select`
  height: 34px;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  background: ${theme.colors.surface};
  color: ${theme.colors.textSecondary};
  font-size: 12.5px;
  padding: 0 ${theme.spacing[3]};
  cursor: pointer;
`;

const DocPick = styled.label<{ $on: boolean; $archived?: boolean }>`
  display: flex; align-items: flex-start; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  cursor: ${p => (p.$archived ? 'not-allowed' : 'pointer')};
  background: ${p => (p.$on ? theme.colors.primary[50] : 'transparent')};
  opacity: ${p => (p.$archived ? 0.5 : 1)};
  transition: background 130ms ease;
  &:last-child { border-bottom: 0; }
  &:hover { background: ${p => (p.$archived ? 'transparent' : p.$on ? theme.colors.primary[100] : theme.colors.backgroundSecondary)}; }
`;

const Box = styled.span<{ $on: boolean }>`
  flex: 0 0 auto; margin-top: 2px;
  width: 17px; height: 17px; border-radius: 5px;
  display: grid; place-items: center;
  border: 1.5px solid ${p => (p.$on ? theme.colors.primary[500] : theme.colors.borderHover)};
  background: ${p => (p.$on ? theme.colors.primary[500] : theme.colors.surface)};
  color: #fff;
`;

const HiddenCheck = styled.input`
  position: absolute; opacity: 0; width: 0; height: 0;
`;

const DocMain = styled.div`min-width: 0; flex: 1;`;

const DocTitle = styled.div`
  font-size: 13px; font-weight: 600; color: ${theme.colors.textPrimary};
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;

const DocSub = styled.div`
  font-size: 11.5px; color: ${theme.colors.textMuted}; margin-top: 3px;
  font-variant-numeric: tabular-nums;
`;

const DocNote = styled.div`
  font-size: 11.5px; color: ${theme.colors.textDisabled}; margin-top: 3px; font-style: italic;
`;

const CritRow = styled.div<{ $open?: boolean }>`
  border-bottom: 1px solid ${theme.colors.borderLight};
  &:last-child { border-bottom: 0; }
`;

const CritHead = styled.div`
  width: 100%; display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]}; background: none; border: 0; cursor: pointer; text-align: left;
  &:hover { background: ${theme.colors.backgroundSecondary}; }
  &:focus-visible { outline: 2px solid ${theme.colors.primary[400]}; outline-offset: -2px; }
`;

const CritId = styled.span`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11px; font-weight: 700; color: ${theme.colors.primary[500]};
  background: ${theme.colors.primary[50]}; border: 1px solid ${theme.colors.primary[200]};
  padding: 2px 7px; border-radius: 6px; flex: 0 0 auto;
`;

const CritName = styled.span`font-size: 13.5px; font-weight: 600; color: ${theme.colors.textPrimary};`;
const CritWeight = styled.span`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 17px; font-weight: 700; color: ${theme.colors.textPrimary};
  font-variant-numeric: tabular-nums; margin-left: auto; flex: 0 0 auto;
`;

const SubList = styled.div`background: ${theme.colors.backgroundSecondary}; padding: ${theme.spacing[2]} 0;`;
const SubRow = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]} ${theme.spacing[2]} ${theme.spacing[10]};
  font-size: 12.5px; color: ${theme.colors.textSecondary};
`;

const WeightRow = styled.div`
  display: grid; grid-template-columns: 1fr 130px 66px;
  align-items: center; gap: ${theme.spacing[4]};
  padding: ${theme.spacing[3]} 0;
  border-bottom: 1px solid ${theme.colors.borderLight};
  &:last-of-type { border-bottom: 0; }
`;

const Slider = styled.input`
  -webkit-appearance: none; appearance: none;
  width: 100%; height: 4px; border-radius: 2px;
  background: ${theme.colors.backgroundTertiary};
  outline: none; cursor: pointer;
  &::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 16px; height: 16px; border-radius: 50%;
    background: ${theme.colors.primary[500]};
    border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,82,.4); cursor: grab;
  }
  &::-moz-range-thumb {
    width: 16px; height: 16px; border-radius: 50%;
    background: ${theme.colors.primary[500]}; border: 2px solid #fff; cursor: grab;
  }
`;

const WeightVal = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 16px; font-weight: 700; text-align: right;
  color: ${theme.colors.textPrimary}; font-variant-numeric: tabular-nums;
`;

const ScaleRow = styled.div`
  display: flex; align-items: flex-start; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} 0;
  font-size: 12.5px; color: ${theme.colors.textSecondary}; line-height: 1.55;
`;

const ScaleBand = styled.span<{ $b: number }>`
  flex: 0 0 auto; width: 24px; height: 24px; border-radius: 7px;
  display: grid; place-items: center; font-size: 12px; font-weight: 700;
  background: ${p => ['#FEE2E2', '#FFEDD5', '#FEF3C7', '#E1E1FD', '#DCFCE7'][p.$b]};
  color: ${p => ['#B91C1C', '#C2410C', '#A16207', '#000052', '#15803D'][p.$b]};
`;

const IssuerEvaluationFlow: React.FC = () => {
  const [step, setStep] = useState(0);

  /* Each step is a state change, not a navigation, so the router's scroll
     reset does not fire. Advancing from the foot of a long step would
     otherwise drop you into the middle of the next one. */
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);
  const [extracted, setExtracted] = useState(false);
  const [open, setOpen] = useState<string | null>('Q1');
  const [weights, setWeights] = useState<Record<string, number>>(
    Object.fromEntries(criteria.map(c => [c.id, c.weight]))
  );
  const [published, setPublished] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  /* The publish step compiles the model document, so show the compile. */
  const goStep = (n: number) => {
    setStep(n);
    if (n !== 3) { setWorking(false); return; }
    setWorking(true);
    window.setTimeout(() => setWorking(false), 6500);
  };
  const [source, setSource] = useState<null | 'upload' | 'bank'>(null);
  const [uploaded, setUploaded] = useState<{ name: string; size: string }[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [procFilter, setProcFilter] = useState('all');

  const procurements = Array.from(new Set(tenderBank.map(d => d.procurementRef)));

  const visibleDocs = tenderBank.filter(d => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || d.name.toLowerCase().includes(q) || d.kind.toLowerCase().includes(q) || d.procurement.toLowerCase().includes(q);
    const matchP = procFilter === 'all' || d.procurementRef === procFilter;
    return matchQ && matchP;
  });

  const toggleDoc = (id: string, archived: boolean) => {
    if (archived) return;
    setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  };

  const pickedDocs = tenderBank.filter(d => picked.includes(d.id));
  const missingRequired = requiredDocIds.filter(id => !picked.includes(id));
  const pickedPages = pickedDocs.reduce((a, d) => a + d.pages, 0);
  const extraneous = pickedDocs.filter(d => !d.required);

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    setUploaded(u => [
      ...u,
      ...Array.from(files).map(f => ({ name: f.name, size: `${(f.size / 1048576).toFixed(1)} MB` })),
    ]);
  };

  /* Whichever source is active decides whether extraction can run. */
  const readyToRun = source === 'bank' ? missingRequired.length === 0 : uploaded.length > 0;

  const total = useMemo(() => Object.values(weights).reduce((a, b) => a + b, 0), [weights]);
  const qualityTotal = useMemo(
    () => criteria.filter(c => c.kind === 'quality').reduce((a, c) => a + weights[c.id], 0),
    [weights]
  );
  const socialValue = weights['Q4'] ?? 0;

  const checks = [
    { ok: total === 100, label: 'Award criteria total 100%', detail: `Currently ${total}%` },
    { ok: socialValue >= 10, label: 'Social value ≥ 10% (PPN 002)', detail: `Currently ${socialValue}% — mandatory for in-scope authorities since 1 Oct 2025` },
    { ok: true, label: 'Award basis is MAT', detail: 'Required under the Procurement Act 2023' },
    { ok: true, label: 'Scoring scale published with definitions', detail: '0–4 banded, defined at ITT §5.7' },
    { ok: true, label: 'Price methodology stated', detail: criteria.find(c => c.kind === 'price')?.method || '' },
  ];
  const blocking = checks.filter(c => !c.ok);

  const meterSegments = criteria.map((c, i) => ({
    label: c.name, value: weights[c.id], color: c.kind === 'price' ? '#F59E0B' : SEG_COLORS[i % 4],
  }));

  return (
    <Shell>
      <HeaderRow>
        <div>
          <Eyebrow>Evaluation · Issuer</Eyebrow>
          <PageTitle>Build the evaluation model</PageTitle>
          <PageSub>
            Extract award criteria, sub-criteria and weightings from the tender pack, then
            validate the model against the regime before it goes out to market.
          </PageSub>
        </div>
        <Actions>
          <Tag $tone="info"><Scale size={11} /> {tender.awardBasis}</Tag>
          <Button as={Link} to="/evaluation" $variant="outline" $size="sm"><ArrowLeft size={14} /> Workspace</Button>
        </Actions>
      </HeaderRow>

      {/* Nothing about the procurement is established until the pack has been
          read, so the record appears only once extraction has produced it. */}
      {extracted && (
        <ContextBar>
          <ContextGrid>
            <ContextItem label="Reference" value={tender.reference} mono />
            <ContextItem label="Procurement" value={tender.title} />
            <ContextItem label="Authority" value={tender.buyer} />
            <ContextItem label="Procedure" value={tender.procedure} />
            <ContextItem label="Regime" value={tender.regime} />
            <ContextItem label="Estimated value" value={`£${(tender.estimatedValue / 1e6).toFixed(2)}m`} />
            <ContextItem label="FTS notice" value={tender.ftsNotice} mono />
          </ContextGrid>
        </ContextBar>
      )}

      <Steps labels={STEPS} current={step} onJump={goStep} />

      {/* ------------------------------------------------ 0 · intake ----- */}
      {step === 0 && source === null && (
        <>
          <SourceGrid>
            <SourceCard onClick={() => setSource('upload')}>
              <SourceIcon><UploadCloud size={20} /></SourceIcon>
              <SourceTitle>Upload documents <ArrowRight size={15} /></SourceTitle>
              <SourceBody>
                Add the tender pack from your machine. Files are indexed on upload and kept
                with this procurement in the Tender Bank.
              </SourceBody>
              <SourceMeta>PDF · DOCX · XLSX — up to 50 MB per file</SourceMeta>
            </SourceCard>

            <SourceCard onClick={() => setSource('bank')}>
              <SourceIcon><Database size={20} /></SourceIcon>
              <SourceTitle>Select from Tender Bank <ArrowRight size={15} /></SourceTitle>
              <SourceBody>
                Use documents already indexed. Pick the pack for this procurement without
                re-uploading anything.
              </SourceBody>
              <SourceMeta>{tenderBank.filter(d => d.status === 'indexed').length} documents indexed</SourceMeta>
            </SourceCard>
          </SourceGrid>

          <Callout $tone="info">
            <Info size={17} style={{ flex: '0 0 auto' }} />
            <div>
              Criteria extraction needs the ITT, the specification, the evaluation model,
              the social value schedule and the pricing matrix. Other documents can be
              included but will not contribute criteria or weightings.
            </div>
          </Callout>
        </>
      )}

      {step === 0 && source === 'upload' && (
        <>
          <Split $ratio="1.55fr 1fr">
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><UploadCloud size={15} /> Upload the tender package</PanelTitle>
                  <PanelNote>
                    <ChangeSource onClick={() => { setSource(null); setUploaded([]); }}>
                      <ArrowLeft size={12} /> Choose a different source
                    </ChangeSource>
                  </PanelNote>
                </div>
                {uploaded.length > 0 && <Tag $tone="ok">{uploaded.length} added</Tag>}
              </PanelHead>
              <PanelBody $flush>
                <Drop
                  $over={dragOver}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                >
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,.xlsx"
                    style={{ display: 'none' }}
                    onChange={e => addFiles(e.target.files)}
                  />
                  <UploadCloud size={26} color={theme.colors.primary[400]} />
                  <DropTitle>Drop files here, or browse</DropTitle>
                  <DropHint>PDF, DOCX and XLSX up to 50 MB each</DropHint>
                </Drop>
                {uploaded.map((f, i) => (
                  <UpRow key={i}>
                    <FileText size={15} color={theme.colors.primary[400]} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                      <div style={{ fontSize: 11.5, color: theme.colors.textMuted }}>{f.size}</div>
                    </div>
                    <Tag $tone="ok">indexed</Tag>
                    <Button $variant="ghost" $size="sm" onClick={() => setUploaded(u => u.filter((_, x) => x !== i))} aria-label={`Remove ${f.name}`}>
                      <X size={14} />
                    </Button>
                  </UpRow>
                ))}
              </PanelBody>
            </Panel>

            <Panel>
              <PanelHead><PanelTitle><Info size={15} /> Procurement setup</PanelTitle></PanelHead>
              <PanelBody>
                <div style={{ display: 'grid', gap: theme.spacing[4] }}>
                  <div><MicroLabel>Procedure</MicroLabel><div style={{ fontSize: 13, fontWeight: 600 }}>{tender.procedure}</div></div>
                  <div><MicroLabel>Award basis</MicroLabel><div style={{ fontSize: 13, fontWeight: 600 }}>{tender.awardBasis}</div></div>
                  <div><MicroLabel>Contract term</MicroLabel><div style={{ fontSize: 13, fontWeight: 600 }}>{tender.term}</div></div>
                </div>
              </PanelBody>
            </Panel>
          </Split>

          <Actions>
            <Button onClick={() => setStep(1)} disabled={!readyToRun}>
              <Sparkles size={15} /> Run extraction
            </Button>
            <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
              {uploaded.length === 0 ? 'Add the tender pack to begin.' : `${uploaded.length} document${uploaded.length === 1 ? '' : 's'} ready.`}
            </span>
          </Actions>
        </>
      )}

      {step === 0 && source === 'bank' && (
        <>
          <Split $ratio="1.55fr 1fr">
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><Database size={15} /> Select from Tender Bank</PanelTitle>
                  <PanelNote>
                    <ChangeSource onClick={() => { setSource(null); setPicked([]); }}>
                      <ArrowLeft size={12} /> Choose a different source
                    </ChangeSource>
                  </PanelNote>
                </div>
                <Tag $tone={missingRequired.length ? 'muted' : 'ok'}>
                  {picked.length} of {tenderBank.length} selected
                </Tag>
              </PanelHead>

              <PickerBar>
                <SearchWrap>
                  <Search size={14} />
                  <SearchBox
                    placeholder="Search documents"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    aria-label="Search the tender bank"
                  />
                </SearchWrap>
                <FilterSel value={procFilter} onChange={e => setProcFilter(e.target.value)} aria-label="Filter by procurement">
                  <option value="all">All procurements</option>
                  {procurements.map(r => <option key={r} value={r}>{r}</option>)}
                </FilterSel>
                <Button
                  $variant="outline"
                  $size="sm"
                  onClick={() => setPicked(requiredDocIds)}
                >
                  Select the full pack
                </Button>
                {picked.length > 0 && (
                  <Button $variant="ghost" $size="sm" onClick={() => setPicked([])}>Clear</Button>
                )}
              </PickerBar>

              <PanelBody $flush>
                {visibleDocs.length === 0 && (
                  <div style={{ padding: theme.spacing[6], textAlign: 'center', color: theme.colors.textMuted, fontSize: 13 }}>
                    No documents match that search. Try a different term or clear the procurement filter.
                  </div>
                )}
                {visibleDocs.map(d => {
                  const on = picked.includes(d.id);
                  const archived = d.status === 'archived';
                  return (
                    <DocPick key={d.id} $on={on} $archived={archived}>
                      <HiddenCheck
                        type="checkbox"
                        checked={on}
                        disabled={archived}
                        onChange={() => toggleDoc(d.id, archived)}
                      />
                      <Box $on={on}>{on && <Check size={12} strokeWidth={3} />}</Box>
                      <DocMain>
                        <DocTitle>
                          {d.name}
                          {d.required && <Tag $tone="info">Required</Tag>}
                          {archived && <Tag $tone="muted">Archived</Tag>}
                        </DocTitle>
                        <DocSub>
                          {d.kind} · {d.pages} pages · {d.size} · <Mono>{d.procurementRef}</Mono> · added {d.addedAt}
                        </DocSub>
                        {d.note && <DocNote>{d.note}</DocNote>}
                      </DocMain>
                    </DocPick>
                  );
                })}
              </PanelBody>
            </Panel>

            <div>
              <Panel>
                <PanelHead><PanelTitle><Layers size={15} /> Extraction input</PanelTitle></PanelHead>
                <PanelBody>
                  {picked.length === 0 ? (
                    <p style={{ fontSize: 12.5, color: theme.colors.textMuted, margin: 0, lineHeight: 1.6 }}>
                      Nothing selected yet. Pick the ITT, the specification, the evaluation model,
                      the social value schedule and the pricing matrix — or use the shortcut above.
                    </p>
                  ) : (
                    <>
                      <TileRow $cols={2} style={{ marginBottom: theme.spacing[4] }}>
                        <StatTile label="Documents" value={picked.length} />
                        <StatTile label="Pages" value={pickedPages.toLocaleString('en-GB')} />
                      </TileRow>
                      <MicroLabel>Selected</MicroLabel>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {pickedDocs.map(d => (
                          <div key={d.id} style={{ fontSize: 12, color: theme.colors.textSecondary, display: 'flex', gap: 7 }}>
                            <FileText size={12} style={{ flex: '0 0 auto', marginTop: 2, color: theme.colors.primary[400] }} />
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </PanelBody>
              </Panel>

              <Panel>
                <PanelHead><PanelTitle><Info size={15} /> Procurement setup</PanelTitle></PanelHead>
                <PanelBody>
                  <div style={{ display: 'grid', gap: theme.spacing[4] }}>
                    <div><MicroLabel>Procedure</MicroLabel><div style={{ fontSize: 13, fontWeight: 600 }}>{tender.procedure}</div></div>
                    <div><MicroLabel>Award basis</MicroLabel><div style={{ fontSize: 13, fontWeight: 600 }}>{tender.awardBasis}</div></div>
                    <div><MicroLabel>Contract term</MicroLabel><div style={{ fontSize: 13, fontWeight: 600 }}>{tender.term}</div></div>
                  </div>
                </PanelBody>
              </Panel>
            </div>
          </Split>

          {missingRequired.length > 0 && picked.length > 0 && (
            <Callout $tone="warn" style={{ marginBottom: theme.spacing[5] }}>
              <AlertTriangle size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>{missingRequired.length} required document{missingRequired.length === 1 ? '' : 's'} missing.</strong>{' '}
                Criteria cannot be extracted without{' '}
                {tenderBank.filter(d => missingRequired.includes(d.id)).map(d => d.name).join(', ')}.
              </div>
            </Callout>
          )}

          {extraneous.length > 0 && missingRequired.length === 0 && (
            <Callout $tone="info" style={{ marginBottom: theme.spacing[5] }}>
              <Info size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>{extraneous.length} document{extraneous.length === 1 ? '' : 's'} carry no award criteria.</strong>{' '}
                {extraneous.map(d => d.name).join(', ')} will be indexed but will not contribute criteria or weightings.
              </div>
            </Callout>
          )}

          <Actions>
            <Button onClick={() => setStep(1)} disabled={!readyToRun}>
              <Sparkles size={15} /> Run extraction
            </Button>
            <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
              {picked.length === 0
                ? 'Select the tender package to begin.'
                : missingRequired.length > 0
                  ? 'Add the missing required documents to continue.'
                  : `${picked.length} documents · ${pickedPages.toLocaleString('en-GB')} pages · typical run 8–12 seconds.`}
            </span>
          </Actions>
        </>
      )}

      {/* -------------------------------------------- 1 · extraction ----- */}
      {step === 1 && (
        <>
          <div style={{ marginBottom: theme.spacing[5] }}>
            <AgentRunConsole
              title="Criteria extraction"
              subtitle={`${tender.reference} · ${picked.length} documents · ${pickedPages.toLocaleString('en-GB')} pages`}
              agents={issuerAgents}
              log={issuerLog}
              onComplete={() => setExtracted(true)}
            />
          </div>

          {extracted && (
            <>
              <TileRow $cols={4} style={{ marginBottom: theme.spacing[5] }}>
                <StatTile label="Criteria extracted" value={criteria.length} foot={`${criteria.reduce((a, c) => a + c.sub.length, 0)} sub-criteria`} />
                <StatTile label="Mean confidence" value="0.94" foot="Lowest 0.90 · Q3.2" />
                <StatTile label="Pages indexed" value={pickedPages.toLocaleString('en-GB')} foot="All citations page-anchored" />
                <StatTile label="Regime checks" value="3 / 3" foot="Procurement Act 2023" accent />
              </TileRow>

              <Split $ratio="1.4fr 1fr">
                <Panel>
                  <PanelHead>
                    <div>
                      <PanelTitle><Scale size={15} /> Extracted award criteria</PanelTitle>
                      <PanelNote>Every node traced to a clause and page in the source pack.</PanelNote>
                    </div>
                  </PanelHead>
                  <PanelBody $flush>
                    {criteria.map(c => (
                      <CritRow key={c.id}>
                        <CritHead
                          role="button"
                          tabIndex={0}
                          aria-expanded={open === c.id}
                          onClick={() => setOpen(open === c.id ? null : c.id)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(open === c.id ? null : c.id); } }}
                        >
                          {c.sub.length > 0 ? <Chevron size={14} $open={open === c.id} color={theme.colors.textMuted} /> : <span style={{ width: 14 }} />}
                          <CritId>{c.id}</CritId>
                          <div style={{ minWidth: 0 }}>
                            <CritName>{c.name}</CritName>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                              <Cite doc={c.source.doc} clause={c.source.clause} page={c.source.page} />
                              <Confidence value={c.confidence} />
                              {c.statutory && <Tag $tone="info"><ShieldCheck size={10} /> {c.statutory}</Tag>}
                            </div>
                          </div>
                          <CritWeight>{c.weight}%</CritWeight>
                        </CritHead>
                        {open === c.id && c.sub.length > 0 && (
                          <SubList>
                            {c.sub.map(s => (
                              <SubRow key={s.id}>
                                <Mono>{s.id}</Mono>
                                <span style={{ flex: 1 }}>{s.name}</span>
                                <Cite doc={s.source.doc} clause={s.source.clause} page={s.source.page} />
                                <Confidence value={s.confidence} />
                                <Num $size="13px" style={{ width: 34, textAlign: 'right' }}>{s.weight}%</Num>
                              </SubRow>
                            ))}
                          </SubList>
                        )}
                      </CritRow>
                    ))}
                  </PanelBody>
                </Panel>

                <div>
                  <Panel>
                    <PanelHead><PanelTitle>Scoring scale</PanelTitle></PanelHead>
                    <PanelBody>
                      {scoringScale.map(s => (
                        <ScaleRow key={s.band}>
                          <ScaleBand $b={s.band}>{s.band}</ScaleBand>
                          <div><strong style={{ color: theme.colors.textPrimary }}>{s.label}</strong> — {s.definition}</div>
                        </ScaleRow>
                      ))}
                    </PanelBody>
                  </Panel>

                  <Panel>
                    <PanelHead>
                      <div>
                        <PanelTitle>Requirement signals</PanelTitle>
                        <PanelNote>Specification detail that should drive scoring.</PanelNote>
                      </div>
                    </PanelHead>
                    <PanelBody $flush>
                      <TableScroll>
                        <Table>
                          <tbody>
                            {ittEvidence.map((e, i) => (
                              <tr key={i}>
                                <td style={{ width: 42 }}><Mono>{e.criterion}</Mono></td>
                                <td>{e.signal}<div style={{ marginTop: 4 }}><Mono>{e.source}</Mono></div></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableScroll>
                    </PanelBody>
                  </Panel>
                </div>
              </Split>

              <Actions>
                <Button onClick={() => setStep(2)}>Review weighting model <ArrowRight size={15} /></Button>
                <Button $variant="outline" onClick={() => { setExtracted(false); setStep(0); setSource(null); setPicked([]); setUploaded([]); }}><RotateCcw size={14} /> Re-run</Button>
              </Actions>
            </>
          )}
        </>
      )}

      {/* ---------------------------------------------- 2 · weights ------ */}
      {step === 2 && (
        <>
          <Split $ratio="1.4fr 1fr">
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><Scale size={15} /> Weighting model</PanelTitle>
                  <PanelNote>Adjust the published weightings. Validation runs live against the regime.</PanelNote>
                </div>
                <Tag $tone={total === 100 ? 'ok' : 'bad'}>Total {total}%</Tag>
              </PanelHead>
              <PanelBody>
                <div style={{ marginBottom: theme.spacing[5] }}>
                  <Meter segments={meterSegments} />
                  <Legend>
                    {meterSegments.map((s, i) => (
                      <LegendItem key={s.label} $c={s.color}>{s.label} {s.value}%</LegendItem>
                    ))}
                  </Legend>
                </div>

                {criteria.map(c => (
                  <WeightRow key={c.id}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CritId>{c.id}</CritId>
                        <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textPrimary }}>{c.name}</span>
                      </div>
                      {c.statutory && (
                        <div style={{ marginTop: 5 }}>
                          <Tag $tone={socialValue >= 10 ? 'ok' : 'bad'}><ShieldCheck size={10} /> {c.statutory}</Tag>
                        </div>
                      )}
                    </div>
                    <Slider
                      type="range" min={0} max={60} step={1}
                      value={weights[c.id]}
                      onChange={e => setWeights(w => ({ ...w, [c.id]: Number(e.target.value) }))}
                    />
                    <WeightVal>{weights[c.id]}%</WeightVal>
                  </WeightRow>
                ))}
              </PanelBody>
            </Panel>

            <div>
              <TileRow $cols={2} style={{ marginBottom: theme.spacing[5] }}>
                <StatTile label="Quality" value={`${qualityTotal}%`} foot="Sum of Q1–Q4" accent />
                <StatTile label="Price" value={`${weights['P1']}%`} foot="Lowest-price ratio" />
              </TileRow>

              <Panel>
                <PanelHead><PanelTitle><ShieldCheck size={15} /> Model validation</PanelTitle></PanelHead>
                <PanelBody $flush>
                  {checks.map(c => (
                    <div key={c.label} style={{
                      display: 'flex', gap: 10, padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                      borderBottom: `1px solid ${theme.colors.borderLight}`, alignItems: 'flex-start',
                    }}>
                      {c.ok
                        ? <CheckCircle2 size={15} color={theme.colors.success[600]} style={{ flex: '0 0 auto', marginTop: 1 }} />
                        : <AlertTriangle size={15} color={theme.colors.error[600]} style={{ flex: '0 0 auto', marginTop: 1 }} />}
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.colors.textPrimary }}>{c.label}</div>
                        <div style={{ fontSize: 11.5, color: theme.colors.textMuted, marginTop: 2 }}>{c.detail}</div>
                      </div>
                    </div>
                  ))}
                </PanelBody>
              </Panel>
            </div>
          </Split>

          {blocking.length > 0 && (
            <Callout $tone="bad" style={{ marginBottom: theme.spacing[5] }}>
              <AlertTriangle size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>{blocking.length} issue{blocking.length > 1 ? 's' : ''} must be resolved before publication.</strong>{' '}
                {blocking.map(b => b.label).join(' · ')}
              </div>
            </Callout>
          )}

          <Actions>
            <Button onClick={() => goStep(3)} disabled={blocking.length > 0}>
              Validate & publish <ArrowRight size={15} />
            </Button>
            <Button $variant="outline" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</Button>
            <Button $variant="ghost" onClick={() => setWeights(Object.fromEntries(criteria.map(c => [c.id, c.weight])))}>
              <RotateCcw size={14} /> Reset to extracted
            </Button>
          </Actions>
        </>
      )}

      {/* ---------------------------------------------- 3 · publish ------ */}
      {step === 3 && working && (
        <Processing
          title="Compiling the evaluation model"
          duration={6500}
          lines={[
            'Validating criteria weightings sum to 100%',
            'Checking social value against the PPN 002 minimum',
            'Resolving sub-criteria under each parent criterion',
            'Writing the 0–4 scoring bands with definitions',
            'Recording the price formula and affordability ceiling',
            'Assembling the document with source citations',
            'Preparing the tender pack attachment',
          ]}
        />
      )}

      {step === 3 && !working && (
        <>
          <Callout $tone="ok" style={{ marginBottom: theme.spacing[5] }}>
            <CheckCircle2 size={17} style={{ flex: '0 0 auto' }} />
            <div>
              <strong>Model validated.</strong> {criteria.length} criteria and{' '}
              {criteria.reduce((a, c) => a + c.sub.length, 0)} sub-criteria totalling {total}%, with social value at{' '}
              {socialValue}%. Ready to publish to the tender pack and to the scoring workspace.
            </div>
          </Callout>

          <TileRow $cols={4} style={{ marginBottom: theme.spacing[5] }}>
            <StatTile label="Criteria published" value={criteria.length} foot={`${criteria.reduce((a, c) => a + c.sub.length, 0)} sub-criteria`} />
            <StatTile label="Quality / price" value={`${qualityTotal} / ${weights['P1']}`} foot="Split of 100 points" />
            <StatTile label="Social value" value={`${socialValue}%`} foot="PPN 002 minimum met" accent />
            <StatTile label="Scoring scale" value="0–4" foot="Banded, definitions published" />
          </TileRow>

          <Panel>
            <PanelHead>
              <div>
                <PanelTitle><Send size={15} /> Evaluation model document</PanelTitle>
                <PanelNote>Issued with the tender pack and used as the scoring basis by the panel.</PanelNote>
              </div>
            </PanelHead>
            <PanelBody>
              <FinalReportViewer
                finalReport={{
                  date: new Date().toISOString(),
                  overall_score: 0,
                  max_score: 100,
                  evaluation_criteria: [],
                  submission_justification: '',
                  refined_responses: [],
                  full_report_markdown: buildModelMarkdown(weights, total, qualityTotal),
                }}
              />
            </PanelBody>
          </Panel>

          {published && (
            <Callout $tone="ok" style={{ marginBottom: theme.spacing[5] }}>
              <CheckCircle2 size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>Model published to {tender.reference}.</strong> Version 1.0, issued {published}.
                It is now the sole basis on which the panel may score, and is attached to the tender
                pack for suppliers. Any change from here is a formal amendment.
              </div>
            </Callout>
          )}

          <Actions>
            {!published ? (
              <>
                <Button onClick={() => setPublished(new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}>
                  <Send size={15} /> Publish model to {tender.reference}
                </Button>
                <Button as={Link} to="/evaluation" $variant="outline">
                  <ArrowLeft size={14} /> Back to workspace
                </Button>
              </>
            ) : (
              <>
                <Button as={Link} to="/evaluation/response-scoring">
                  Proceed to Score Responses <ArrowRight size={15} />
                </Button>
                <Button as={Link} to="/evaluation" $variant="outline">
                  <ArrowLeft size={14} /> Back to workspace
                </Button>
              </>
            )}
            <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
              {published
                ? 'Scoring opens once tenders are received.'
                : 'Publishing attaches the model to the tender pack and locks it for the evaluation.'}
            </span>
          </Actions>
        </>
      )}
    </Shell>
  );
};

function buildModelMarkdown(weights: Record<string, number>, total: number, quality: number): string {
  const L: string[] = [];
  L.push(`# Evaluation Model — ${tender.reference}`);
  L.push(`**Procurement:** ${tender.title}`);
  L.push(`**Contracting authority:** ${tender.buyer} (${tender.buyerType})`);
  L.push(`**Procedure:** ${tender.procedure} under the ${tender.regime}`);
  L.push(`**Award basis:** ${tender.awardBasis}`);
  L.push(`**Notice:** ${tender.ftsNotice}`);
  L.push('');
  L.push(`> This document sets out the criteria, sub-criteria, weightings and scoring methodology against which tenders for ${tender.reference} will be assessed. It is issued with the tender pack and is the sole basis on which the evaluation panel may score.`);
  L.push('');
  L.push(`## 1. Award criteria and weightings`);
  L.push('');
  L.push(`| Ref | Criterion | Weighting | Source |`);
  L.push(`| --- | --- | ---: | --- |`);
  criteria.forEach(c => L.push(`| ${c.id} | ${c.name} | ${weights[c.id]}% | ${c.source.doc}, ${c.source.clause}, p.${c.source.page} |`));
  L.push(`| | **Total** | **${total}%** | |`);
  L.push('');
  L.push(`Quality carries ${quality} of the 100 available points; price carries ${weights['P1']}.`);
  L.push('');
  L.push(`## 2. Sub-criteria`);
  L.push('');
  criteria.filter(c => c.sub.length).forEach(c => {
    L.push(`### ${c.id} · ${c.name} — ${weights[c.id]}%`);
    L.push('');
    L.push(`| Ref | Sub-criterion | Weighting | Confidence | Source |`);
    L.push(`| --- | --- | ---: | ---: | --- |`);
    c.sub.forEach(s => L.push(`| ${s.id} | ${s.name} | ${s.weight}% | ${s.confidence.toFixed(2)} | ${s.source.doc}, ${s.source.clause}, p.${s.source.page} |`));
    L.push('');
  });
  L.push(`## 3. Scoring methodology`);
  L.push('');
  L.push(`Quality sub-criteria are scored on the following 0–4 banded scale. The band awarded is converted to points as \`band ÷ 4 × sub-criterion weighting\`.`);
  L.push('');
  L.push(`| Band | Label | Definition |`);
  L.push(`| :---: | --- | --- |`);
  scoringScale.forEach(s => L.push(`| ${s.band} | ${s.label} | ${s.definition} |`));
  L.push('');
  L.push(`Price is scored as **${criteria.find(c => c.kind === 'price')?.method}**. The lowest *compliant* tendered price sets the benchmark; a tender excluded at the compliance gate does not set the benchmark for the tenders that remain.`);
  L.push('');
  L.push(`## 4. Compliance gate`);
  L.push('');
  L.push(`Tenders are assessed against mandatory requirements before any quality assessment is undertaken. A tender that fails a mandatory requirement, or to which a mandatory exclusion ground in Schedule 6 of the Procurement Act 2023 applies, is excluded and is neither scored nor ranked.`);
  L.push('');
  L.push(`## 5. Regulatory basis`);
  L.push('');
  L.push(`- Award is made on the basis of the **Most Advantageous Tender (MAT)** under the Procurement Act 2023, which came into force on 24 February 2025 and replaced the Public Contracts Regulations 2015.`);
  L.push(`- Social value carries **${weights['Q4']}%** of the total available marks, meeting the minimum 10% weighting required by **PPN 002**, mandatory for in-scope contracting authorities from 1 October 2025.`);
  L.push(`- Each supplier will receive an **assessment summary** setting out their scores and the reasons for them before the contract award notice is published, followed by a standstill period of **8 working days**.`);
  L.push('');
  L.push(`## 6. Indicative timetable`);
  L.push('');
  L.push(`| Stage | Date |`);
  L.push(`| --- | --- |`);
  tender.timeline.forEach(t => L.push(`| ${t.label} | ${t.date}${t.note ? ` (${t.note})` : ''} |`));
  return L.join('\n');
}

export default IssuerEvaluationFlow;
